import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Slider,
  Checkbox,
  Link,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/GppGood';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useMe } from '../api/hooks.js';
import { useT } from '../i18n/useT.js';

type Version = 'v2' | 'v3';
type Action = 'register' | 'login' | 'forgotPassword';
const ACTIONS: Action[] = ['register', 'login', 'forgotPassword'];

interface RecaptchaSettings {
  enabled: boolean;
  siteKey: string;
  secretKeySet: boolean;
  version: Version;
  minScore: number;
  actions: Action[];
}

function errMsg(e: unknown): string {
  return (
    (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
    'Request failed'
  );
}

/**
 * reCAPTCHA keys, entered once for the whole deployment.
 *
 * It sits behind the super-admin gate rather than in a tenant's own Security
 * screen because the forms it guards — registration, sign-in, password reset —
 * are the deployment's, not any one organization's. A tenant could not turn it
 * on for their own login without turning it on for everybody's.
 */
export function AdminRecaptchaPage() {
  const all = useT();
  const t = all.admin.recaptcha;
  const needSuperAdmin = all.admin.needSuperAdmin;
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState('');

  const settings = useQuery({
    enabled: !!me?.isSuperAdmin,
    queryKey: ['admin-recaptcha'],
    queryFn: async () => (await api.get('/v1/admin/recaptcha')).data as RecaptchaSettings,
  });

  const [form, setForm] = useState<RecaptchaSettings>({
    enabled: false,
    siteKey: '',
    secretKeySet: false,
    version: 'v3',
    minScore: 0.5,
    actions: ['register', 'forgotPassword'],
  });

  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: async () =>
      api.put('/v1/admin/recaptcha', {
        enabled: form.enabled,
        siteKey: form.siteKey,
        version: form.version,
        minScore: form.minScore,
        actions: form.actions,
        // Write-only: sent only when a new one was typed, so leaving the field
        // blank keeps what is stored rather than clearing it.
        ...(secretKey ? { secretKey } : {}),
      }),
    onSuccess: () => {
      setSecretKey('');
      qc.invalidateQueries({ queryKey: ['admin-recaptcha'] });
      setToast(t.saved);
    },
    onError: (e) => setToast(errMsg(e)),
  });

  if (me && !me.isSuperAdmin) return <Alert severity="error">{needSuperAdmin}</Alert>;

  const set = <K extends keyof RecaptchaSettings>(k: K, v: RecaptchaSettings[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleAction = (a: Action) =>
    set('actions', form.actions.includes(a) ? form.actions.filter((x) => x !== a) : [...form.actions, a]);

  return (
    <Box sx={{ maxWidth: 680 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <ShieldIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.subtitle}{' '}
        <Link href="https://www.google.com/recaptcha/admin" target="_blank" rel="noreferrer">
          google.com/recaptcha/admin
        </Link>
      </Typography>

      <Paper sx={{ p: 3 }}>
        <FormControlLabel
          control={<Switch checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
          label={t.enable}
        />

        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t.version}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.version}
            onChange={(_e, v) => v && set('version', v as Version)}
          >
            <ToggleButton value="v3">v3 ({t.v3Hint})</ToggleButton>
            <ToggleButton value="v2">v2 ({t.v2Hint})</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            size="small"
            label={t.siteKey}
            value={form.siteKey}
            onChange={(e) => set('siteKey', e.target.value)}
            helperText={t.siteKeyHint}
            fullWidth
          />
          <TextField
            size="small"
            type="password"
            label={t.secretKey}
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            placeholder={form.secretKeySet ? t.secretStored : ''}
            helperText={t.secretKeyHint}
            fullWidth
          />
        </Stack>

        {form.version === 'v3' && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t.minScore}: <b>{form.minScore.toFixed(1)}</b>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t.minScoreHint}
            </Typography>
            <Slider
              size="small"
              min={0}
              max={1}
              step={0.1}
              marks
              value={form.minScore}
              onChange={(_e, v) => set('minScore', v as number)}
              sx={{ maxWidth: 360 }}
            />
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2">{t.protect}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t.protectHint}
        </Typography>
        <Stack>
          {ACTIONS.map((a) => (
            <FormControlLabel
              key={a}
              control={<Checkbox size="small" checked={form.actions.includes(a)} onChange={() => toggleAction(a)} />}
              label={
                <Box>
                  <Typography variant="body2">{t.actions[a]}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.actionHints[a]}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Stack>

        <Alert severity="info" sx={{ mt: 3 }}>
          {t.failOpen}
        </Alert>

        <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
          <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? t.saving : t.save}
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </Box>
  );
}
